import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Acceptance_Status_Enum,
  UpdateAcceptanceMutation,
  UpdateAcceptanceMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateAcceptanceTRPCInput = Omit<
  UpdateAcceptanceMutationVariables,
  'OriginalTimestamp' | 'Title' | 'Details' | 'Status' | 'CustomAttributeData'
> & {
  Title: string;
  Details: string;
  Status: Acceptance_Status_Enum;
  CustomAttributeData?: CustomAttributeData | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
}): UpdateAcceptanceMutation => {
  return {
    updateChildAcceptance: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useUpdateAcceptanceTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.acceptance.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.acceptance.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.acceptance.getById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.acceptance.getByParentRiskId.queryKey(),
        });
      },
    }),
  });

  return {
    updateAcceptance: async (
      variables: UpdateAcceptanceTRPCInput
    ): Promise<UpdateAcceptanceMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
